/**
 * Function Explorer - Sourcetrail-style call graph navigation
 * Issue #462: Primary view for navigating large call graphs
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import type { CallGraphData, CallGraphNode, CallGraphEdge } from '@veodiagram/shared';

interface FunctionExplorerProps {
  data: CallGraphData;
  initialFunctionId?: string;
  selectedFunctionId?: string;
  onSelectFunction?: (functionId: string) => void;
  onFocusFunction?: (functionId: string) => void;
  onOpenSource?: (file: string, line: number) => void;
}

interface NeighborInfo {
  node: CallGraphNode;
  edges: CallGraphEdge[];
  totalCalls: number;
}

export function FunctionExplorer({
  data,
  initialFunctionId,
  selectedFunctionId,
  onSelectFunction,
  onFocusFunction,
  onOpenSource
}: FunctionExplorerProps) {
  // Find initial function - prefer selectedFunctionId, initialFunctionId, first entry point, or first node
  const defaultFunctionId = selectedFunctionId || initialFunctionId || data.entryPoints?.[0] || data.nodes?.[0]?.id;
  const [activeFunctionId, setActiveFunctionId] = useState<string | null>(defaultFunctionId || null);
  const [navigationHistory, setNavigationHistory] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCallers, setExpandedCallers] = useState(true);
  const [expandedCallees, setExpandedCallees] = useState(true);

  // Sync with external selectedFunctionId prop changes
  useEffect(() => {
    if (selectedFunctionId && selectedFunctionId !== activeFunctionId) {
      setActiveFunctionId(selectedFunctionId);
    }
  }, [selectedFunctionId, activeFunctionId]);

  // Build lookup maps
  const nodeMap = useMemo(() => {
    const map = new Map<string, CallGraphNode>();
    for (const node of data.nodes) {
      map.set(node.id, node);
    }
    return map;
  }, [data.nodes]);

  const edgesBySource = useMemo(() => {
    const map = new Map<string, CallGraphEdge[]>();
    for (const edge of data.edges) {
      const existing = map.get(edge.source) || [];
      existing.push(edge);
      map.set(edge.source, existing);
    }
    return map;
  }, [data.edges]);

  const edgesByTarget = useMemo(() => {
    const map = new Map<string, CallGraphEdge[]>();
    for (const edge of data.edges) {
      const existing = map.get(edge.target) || [];
      existing.push(edge);
      map.set(edge.target, existing);
    }
    return map;
  }, [data.edges]);

  // Get active function
  const activeFunction = activeFunctionId ? nodeMap.get(activeFunctionId) : null;

  // Get callers (functions that call the active function)
  const callers = useMemo((): NeighborInfo[] => {
    if (!activeFunctionId) return [];
    const incomingEdges = edgesByTarget.get(activeFunctionId) || [];
    const callerMap = new Map<string, NeighborInfo>();

    for (const edge of incomingEdges) {
      const callerNode = nodeMap.get(edge.source);
      if (!callerNode) continue;

      const existing = callerMap.get(edge.source);
      if (existing) {
        existing.edges.push(edge);
        existing.totalCalls += edge.callCount;
      } else {
        callerMap.set(edge.source, {
          node: callerNode,
          edges: [edge],
          totalCalls: edge.callCount,
        });
      }
    }

    return Array.from(callerMap.values()).sort((a, b) => b.totalCalls - a.totalCalls);
  }, [activeFunctionId, edgesByTarget, nodeMap]);

  // Get callees (functions called by the active function)
  const callees = useMemo((): NeighborInfo[] => {
    if (!activeFunctionId) return [];
    const outgoingEdges = edgesBySource.get(activeFunctionId) || [];
    const calleeMap = new Map<string, NeighborInfo>();

    for (const edge of outgoingEdges) {
      const calleeNode = nodeMap.get(edge.target);
      if (!calleeNode) continue;

      const existing = calleeMap.get(edge.target);
      if (existing) {
        existing.edges.push(edge);
        existing.totalCalls += edge.callCount;
      } else {
        calleeMap.set(edge.target, {
          node: calleeNode,
          edges: [edge],
          totalCalls: edge.callCount,
        });
      }
    }

    return Array.from(calleeMap.values()).sort((a, b) => b.totalCalls - a.totalCalls);
  }, [activeFunctionId, edgesBySource, nodeMap]);

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return data.nodes
      .filter(node =>
        node.name.toLowerCase().includes(query) ||
        node.qualifiedName.toLowerCase().includes(query) ||
        node.module.toLowerCase().includes(query)
      )
      .slice(0, 10);
  }, [searchQuery, data.nodes]);

  // Navigate to a function
  const navigateTo = useCallback((functionId: string) => {
    if (activeFunctionId) {
      setNavigationHistory(prev => [...prev, activeFunctionId]);
    }
    setActiveFunctionId(functionId);
    setSearchQuery('');
    onSelectFunction?.(functionId);
  }, [activeFunctionId, onSelectFunction]);

  // Go back in history
  const goBack = useCallback(() => {
    if (navigationHistory.length > 0) {
      const prev = navigationHistory[navigationHistory.length - 1];
      setNavigationHistory(prev => prev.slice(0, -1));
      setActiveFunctionId(prev);
    }
  }, [navigationHistory]);

  // Get node type color
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'entry': return 'bg-green-100 text-green-800 border-green-300';
      case 'function': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'method': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'arrow': return 'bg-pink-100 text-pink-800 border-pink-300';
      case 'constructor': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'external': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Get type icon
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'entry': return '>';
      case 'function': return 'f';
      case 'method': return 'm';
      case 'arrow': return '=>';
      case 'constructor': return 'C';
      case 'external': return 'E';
      default: return '?';
    }
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Search Bar */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="relative">
          <input
            type="text"
            placeholder="Search functions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          />
          <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        {/* Search Results Dropdown */}
        {searchResults.length > 0 && (
          <div className="absolute z-10 mt-1 w-full max-w-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
            {searchResults.map(node => (
              <button
                key={node.id}
                onClick={() => navigateTo(node.id)}
                className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <span className={`px-2 py-0.5 text-xs rounded border ${getTypeColor(node.type)}`}>
                  {getTypeIcon(node.type)}
                </span>
                <span className="font-medium dark:text-white">{node.name}</span>
                <span className="text-gray-500 text-sm">{node.module}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Navigation Breadcrumb */}
      {navigationHistory.length > 0 && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <button
            onClick={goBack}
            className="px-2 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600 dark:text-white"
          >
            Back
          </button>
          <span className="text-gray-500 dark:text-gray-400 text-sm">
            {navigationHistory.map((id, i) => {
              const node = nodeMap.get(id);
              return (
                <span key={id}>
                  {i > 0 && ' > '}
                  <button
                    onClick={() => {
                      setNavigationHistory(prev => prev.slice(0, i));
                      setActiveFunctionId(id);
                    }}
                    className="hover:underline"
                  >
                    {node?.name || id}
                  </button>
                </span>
              );
            })}
            {' > '}{activeFunction?.name}
          </span>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-4">
        {activeFunction ? (
          <div className="space-y-6">
            {/* Callers Section */}
            <div>
              <button
                onClick={() => setExpandedCallers(!expandedCallers)}
                className="flex items-center gap-2 text-lg font-semibold text-gray-700 dark:text-gray-200 mb-3"
              >
                <span className={`transform transition-transform ${expandedCallers ? 'rotate-90' : ''}`}>
                  {'>'}
                </span>
                CALLERS ({callers.length})
              </button>

              {expandedCallers && (
                <div className="space-y-2 ml-4">
                  {callers.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-sm italic">No callers found (entry point or unused)</p>
                  ) : (
                    callers.map(({ node, totalCalls, edges }) => (
                      <button
                        key={node.id}
                        onClick={() => navigateTo(node.id)}
                        className="w-full p-3 text-left bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs rounded border ${getTypeColor(node.type)}`}>
                              {node.type}
                            </span>
                            <span className="font-medium dark:text-white">{node.name}</span>
                            {edges.some(e => e.isAsync) && (
                              <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">async</span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {totalCalls} call{totalCalls !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center justify-between">
                          <span>{node.module}</span>
                          {node.location && (
                            <span className="text-xs text-gray-400">
                              {node.location.file.split('/').pop()}:{node.location.startLine}
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Active Function Card */}
            <div className="bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-500 rounded-xl p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-3 py-1 text-sm rounded-full border-2 ${getTypeColor(activeFunction.type)}`}>
                      {activeFunction.type}
                    </span>
                    {activeFunction.isAsync && (
                      <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded">async</span>
                    )}
                    {activeFunction.isEntryPoint && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">entry</span>
                    )}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{activeFunction.qualifiedName}</h2>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">{activeFunction.module}</p>
                </div>
                <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                  <div>{callers.length} callers</div>
                  <div>{callees.length} callees</div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-4">
                {activeFunction.location && (
                  <button
                    onClick={() => onOpenSource?.(activeFunction.location!.file, activeFunction.location!.startLine)}
                    className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    {activeFunction.location.file}:{activeFunction.location.startLine}
                  </button>
                )}
                {onFocusFunction && (
                  <button
                    onClick={() => onFocusFunction(activeFunction.id)}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-800/50 transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    View Subgraph
                  </button>
                )}
              </div>

              {activeFunction.complexity && (
                <div className="mt-4 flex gap-4 text-sm">
                  <span className="text-gray-600 dark:text-gray-300">
                    Complexity: <strong>{activeFunction.complexity.cyclomatic}</strong>
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">
                    Branches: <strong>{activeFunction.complexity.branches}</strong>
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">
                    Loops: <strong>{activeFunction.complexity.loops}</strong>
                  </span>
                </div>
              )}
            </div>

            {/* Callees Section */}
            <div>
              <button
                onClick={() => setExpandedCallees(!expandedCallees)}
                className="flex items-center gap-2 text-lg font-semibold text-gray-700 dark:text-gray-200 mb-3"
              >
                <span className={`transform transition-transform ${expandedCallees ? 'rotate-90' : ''}`}>
                  {'>'}
                </span>
                CALLEES ({callees.length})
              </button>

              {expandedCallees && (
                <div className="space-y-2 ml-4">
                  {callees.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-sm italic">No callees found (leaf function)</p>
                  ) : (
                    callees.map(({ node, totalCalls, edges }) => (
                      <button
                        key={node.id}
                        onClick={() => navigateTo(node.id)}
                        className="w-full p-3 text-left bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs rounded border ${getTypeColor(node.type)}`}>
                              {node.type}
                            </span>
                            <span className="font-medium dark:text-white">{node.name}</span>
                            {edges.some(e => e.isAsync) && (
                              <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-700 rounded">async</span>
                            )}
                            {node.isExternal && (
                              <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">external</span>
                            )}
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {totalCalls} call{totalCalls !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center justify-between">
                          <span>{node.module}</span>
                          {node.location && (
                            <span className="text-xs text-gray-400">
                              {node.location.file.split('/').pop()}:{node.location.startLine}
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <p>No function selected</p>
            <p className="text-sm mt-2">Use the search bar to find a function</p>
          </div>
        )}
      </div>

      {/* Stats Footer */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
        {data.nodes?.length || 0} functions | {data.edges?.length || 0} calls | {data.entryPoints?.length || 0} entry points
      </div>
    </div>
  );
}

export default FunctionExplorer;
